import React, { useState } from 'react';
import apiService from '../../services/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface ApproveClalitModalProps {
    isOpen: boolean;
    onClose: () => void;
    task?: any;
    token: string;
    onSuccess?: () => void;
    onStatusMessage?: (message: { type: 'success' | 'error' | 'loading'; text: string } | null) => void;
}

export const ApproveClalitModal: React.FC<ApproveClalitModalProps> = ({
    isOpen,
    onClose,
    task,
    token,
    onSuccess,
    onStatusMessage,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedOption, setSelectedOption] = useState<string>('clalit');
    const [intakeObligation, setIntakeObligation] = useState<string>('');
    const [followupObligation, setFollowupObligation] = useState<string>('');
    const [intakeVerified, setIntakeVerified] = useState<boolean>(false);
    const [followupVerified, setFollowupVerified] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string>('');

    // Reset state when modal opens and populate with task data if present
    React.useEffect(() => {
        if (isOpen && task) {
            // Pre-populate with existing task data if available
            const hasIntake = task.obligation_intake && task.obligation_intake.trim() !== '';
            const hasFollowup = task.obligation_followup && task.obligation_followup.trim() !== '';

            setIntakeObligation(task.obligation_intake || '');
            setFollowupObligation(task.obligation_followup || '');
            setIntakeVerified(task.obligation_verified || false);
            setFollowupVerified(task.followup_obligation_verified || false);

            // Set dropdown based on whether there's obligation data
            if (!hasIntake && !hasFollowup && !task.obligation_verified && !task.followup_obligation_verified) {
                setSelectedOption('clalit');
            } else {
                setSelectedOption('clalit');
            }

            setValidationError('');
        }
    }, [isOpen, task]);

    // Handle save
    const handleSave = async () => {
        if (!task || !token) return;

        // Validation
        if (selectedOption !== 'not_clalit') {
            // Check if at least one checkbox is checked
            if (!intakeVerified && !followupVerified) {
                setValidationError('Please check at least one verification checkbox (Intake or Follow-Up)');
                return;
            }

            if (intakeVerified && !intakeObligation.trim()) {
                setValidationError('Intake obligation is required when verified');
                return;
            }
            if (followupVerified && !followupObligation.trim()) {
                setValidationError('Follow-up obligation is required when verified');
                return;
            }
        }

        setValidationError('');
        setIsSubmitting(true);
        onStatusMessage?.({ type: 'loading', text: 'Saving obligation...' });

        try {
            const response = await apiService.updateObligation({
                token,
                patient_id: task.id,
                obligation_intake: selectedOption === 'not_clalit' ? '' : intakeObligation,
                obligation_followup: selectedOption === 'not_clalit' ? '' : followupObligation,
                intake_obligation_verified: selectedOption === 'not_clalit' ? false : intakeVerified,
                followup_obligation_verified: selectedOption === 'not_clalit' ? false : followupVerified,
                not_maccabi: selectedOption === 'not_clalit',
            });

            console.log('[ApproveClalitModal] Response:', response);
            if (response.op === 'delete') {
                console.log('[ApproveClalitModal] Task will be removed from list');
            }

            onStatusMessage?.({ type: 'success', text: 'Obligation saved successfully' });

            // Auto-clear success message after 3 seconds
            setTimeout(() => {
                onStatusMessage?.(null);
            }, 3000);

            // Always refresh the task list to update or remove the task
            onSuccess?.();

            handleClose();
        } catch (error: any) {
            console.error('Failed to save obligation:', error);
            onStatusMessage?.({
                type: 'error',
                text: error.message || 'Failed to save obligation'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle close
    const handleClose = () => {
        setValidationError('');
        onClose();
    };

    const showObligationFields = selectedOption !== 'not_clalit';

    return (
        <ModalWrapper modalId="ApproveClalitModal"
            isOpen={isOpen}
            onClose={handleClose}
            title="Approve Clalit Patient"
            isLoading={isSubmitting}
        >
            <div className="space-y-4">
                {/* Options Dropdown */}
                <div className="flex items-center gap-4">
                    <label className="font-semibold text-[#366A75] min-w-[100px]">Options</label>
                    <select
                        value={selectedOption}
                        onChange={(e) => setSelectedOption(e.target.value)}
                        className="flex-1 px-4 py-2 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent appearance-none text-[#366A75]"
                    >
                        <option value="clalit">Clalit</option>
                        <option value="not_clalit">Not Clalit</option>
                    </select>
                </div>

                {/* Intake Field */}
                {showObligationFields && (
                    <div className="flex items-center gap-4">
                        <label className="font-semibold text-[#366A75] min-w-[100px]">Intake</label>
                        <input
                            type="text"
                            value={intakeObligation}
                            onChange={(e) => setIntakeObligation(e.target.value)}
                            className={`${POPUP_STYLES.input} flex-1`}
                            placeholder="Enter intake obligation"
                        />
                        <input
                            type="checkbox"
                            checked={intakeVerified}
                            onChange={(e) => setIntakeVerified(e.target.checked)}
                            className="w-5 h-5 text-[#366A75] border-[#B4D9D4] rounded focus:ring-[#366A75]"
                        />
                    </div>
                )}

                {/* Follow-Up Field */}
                {showObligationFields && (
                    <div className="flex items-center gap-4">
                        <label className="font-semibold text-[#366A75] min-w-[100px]">Follow-Up</label>
                        <input
                            type="text"
                            value={followupObligation}
                            onChange={(e) => setFollowupObligation(e.target.value)}
                            className={`${POPUP_STYLES.input} flex-1`}
                            placeholder="Enter follow-up obligation"
                        />
                        <input
                            type="checkbox"
                            checked={followupVerified}
                            onChange={(e) => setFollowupVerified(e.target.checked)}
                            className="w-5 h-5 text-[#366A75] border-[#B4D9D4] rounded focus:ring-[#366A75]"
                        />
                    </div>
                )}

                {/* Validation Error */}
                {validationError && (
                    <div className="text-red-600 text-sm font-medium">
                        {validationError}
                    </div>
                )}

                {/* Buttons */}
                <div className="flex justify-center gap-4 pt-4">
                    <button
                        onClick={handleSave}
                        disabled={isSubmitting}
                        className={POPUP_STYLES.button}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className={POPUP_STYLES.button}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </ModalWrapper>
    );
};
